import gsap from 'gsap'
import Producer from './Producer.js'
import Square from './component/Square.js'
import Carousel from './component/Carousel.js'
import { projects } from '../content/projects.js'
import { DURATION, EASE, STAGGER } from '../shared/motion.js'
import { device } from '../shared/device.js'

// ─────────────────────────────────────────────────────────────
// Transition Mapping & Configurations
// ─────────────────────────────────────────────────────────────

const LOADER_CONFIG = Object.freeze({ hold: 0.5, ramp: 1.6 })

/**
 * 轉場查表：`${fromView}→${toView}` → 對應 Director 的動畫工廠方法名稱
 * 表上未定義的路由組合將被無條件拒絕，防止狀態與 URL 不一致
 */
const TRANSITIONS = Object.freeze({
    'home→detail': { leave: 'leaveHome', enter: 'enterDetail' },
    'detail→home': { leave: 'leaveDetail', enter: 'enterHome' },
    'detail→detail': { leave: 'leaveGallery', enter: 'enterGallery' },
    'home→about': { leave: 'leaveHomeToAbout', enter: 'enterAbout' },
    'about→home': { leave: 'leaveAbout', enter: 'enterHome' },
    'detail→about': { leave: 'leaveDetailToAbout', enter: 'enterAbout' },
    'about→detail': { leave: 'leaveAbout', enter: 'enterDetailFromAbout' }
})

// ─────────────────────────────────────────────────────────────
// Director (Core Controller)
// ─────────────────────────────────────────────────────────────

export default class Director {
    constructor() {
        this.studio = new Producer()

        // 核心單例參照
        this.router = this.studio.router
        this.assetLoader = this.studio.assetLoader
        this.renderer = this.studio.renderer
        this.lenis = this.studio.lenis

        // Overlay UI 參照
        this.loader = this.studio.loader
        this.project = this.studio.project
        this.detail = this.studio.detail
        this.about = this.studio.about
        this.footer = this.studio.footer
        this.header = this.studio.header

        // 3D WebGL 元件實例
        this.squares = []
        this.carousel = null

        // 路由狀態機管線
        this._isTransitioning = false
        this.navTl = null
        this.introTl = null

        // 當前畫面實際代表的路徑（轉場一開始就更新，早於動畫播完）
        this._currentPath = this.router.currentPath
        this._lastBreakpoint = device.breakpoint

        // 當前視圖狀態 ('home' | 'detail' | 'about')
        this.activeView = 'home'

        // 監聽瀏覽器上一頁/下一頁 (popstate)
        this.router.onChange = (path, source) => {
            if (source !== 'popstate') {
                return
            }

            // 轉場進行中：丟棄這次 popstate，並把網址列彈回目前畫面對應的路徑，
            // 避免網址跳到新頁但畫面還停在舊頁的分裂狀態
            if (this._isTransitioning) {
                this.router.replace(this._currentPath)
                return
            }

            this._navigate(path, this.router.prev, true)
        }

        this.init()
    }

    // ─────────────────────────────────────────────────────────────
    // ① Initialization Pipeline & Asset Loading
    // ─────────────────────────────────────────────────────────────

    /** 應用程式總進入點：Loader 動畫 -> 貼圖暖機 -> 建立 3D 卡片 -> 著色器編譯 -> 進場 */
    async init() {
        await this._runLoader()

        // 暖機 GPU 貼圖與預先編譯 Shader
        this.assetLoader.warmUp(this.renderer.instance)
        this.spawnAllSquare()
        this.renderer.compile()

        // 執行首頁或 Deep-link 內頁進場動畫
        this.intro()
    }

    /** 驅動載入頁進度條，結合真實 Network 載入與最小動畫時長 */
    _runLoader() {
        return new Promise((resolve) => {
            let realProgress = 0

            this._load((p) => {
                realProgress = p
            }).catch(() => {
                realProgress = 1
            })

            const startTime = performance.now()
            const tick = () => {
                const elapsed = (performance.now() - startTime) / 1000
                if (elapsed < LOADER_CONFIG.hold) {
                    return requestAnimationFrame(tick)
                }

                const ramp = Math.min((elapsed - LOADER_CONFIG.hold) / LOADER_CONFIG.ramp, 1)
                this.loader.setProgress(Math.min(realProgress, ramp))

                if (realProgress >= 1 && ramp >= 1) {
                    return resolve()
                }
                requestAnimationFrame(tick)
            }
            requestAnimationFrame(tick)
        })
    }

    /** 併行載入專案縮圖、內頁圖片與字型 */
    async _load(onProgress) {
        const thumbs = projects.map((p) => {
            return { id: p.id, src: p.thumbnail }
        })
        // 影片不能丟給 ImageBitmapLoader 解碼（會直接觸發 onError 卡住整個載入畫面），
        // 排除後交給 Detail.js 的 <video> 自己串流播放，不佔用這裡的預載進度
        const galleryImages = projects.flatMap((p) => {
            return p.medias
                .filter((media) => {
                    return media.type !== 'video'
                })
                .map((media, i) => {
                    return { id: `${p.id}-img-${i}`, src: media.src }
                })
        })
        const images = [...thumbs, ...galleryImages]
        const fonts = ['400 1em "Switzer"', '700 1em "Switzer"']

        const total = images.length + fonts.length
        let texUnits = 0
        let fontUnits = 0

        const report = () => {
            return onProgress?.((texUnits + fontUnits) / total)
        }

        await Promise.all([
            this.assetLoader.load(images, (progress) => {
                texUnits = progress * images.length
                report()
            }),
            ...fonts.map((font) => {
                return document.fonts.load(font).then(() => {
                    fontUnits++
                    report()
                })
            })
        ])

        onProgress?.(1)
    }

    /** 依據 Projects 內容初始化所有 3D Square 實例 */
    spawnAllSquare() {
        this.squares = projects.map((project, index) => {
            const sq = new Square(0, 0, 0, 0.7, 0.7, project, index)
            const texture = this.assetLoader.getTexture(project.id)

            if (texture) {
                sq.material.uniforms.uTexture.value = texture
                sq.material.uniforms.uTextureAspect.value = texture.image.width / texture.image.height
                sq.material.uniforms.uHasImage.value = 1.0
                sq.mesh.renderOrder = 1 // 讓有圖卡片蓋在佔位符卡片之上
            }
            sq.material.uniforms.uOpacity.value = 0

            return sq
        })
    }

    // ─────────────────────────────────────────────────────────────
    // ② Routing Pipeline & Navigation Controls
    // ─────────────────────────────────────────────────────────────

    /** UI Overlay 觸發導航請求 */
    requestNavigate(path) {
        this._navigate(path, this.router.currentPath, false)
    }

    /** 主轉場管線：鎖定 -> Leave Timeline -> 更新 URL & 狀態 -> Enter Timeline -> 解鎖 */
    _navigate(to, from, urlAlreadyUpdated) {
        if (to === from || this._isTransitioning) {
            return
        }

        const fromView = this._viewOf(from)
        const toView = this._viewOf(to)
        const transition = TRANSITIONS[`${fromView}→${toView}`]
        const ctx = { to, from, project: this._parseProject(to) }

        if (!transition || (toView === 'detail' && !ctx.project?.enable)) {
            return
        }

        // 在動畫開始前就切換 activeView（單一計算來源，衍生自 toView），讓 update() 的
        // per-frame carousel/gallery 分流即時生效，不被離場中的 tween 覆寫
        this.activeView = toView
        this._currentPath = to

        // 切換至 Home 視圖時，將 DOM 捲動歸零
        if (toView === 'home') {
            this.lenis?.scrollTo(0, { immediate: true })
        }

        this.navTl?.kill()
        this._isTransitioning = true

        this.navTl = gsap.timeline({
            onComplete: () => {
                this._isTransitioning = false
            }
        })

        // 1. Leave 動畫
        const leave = this[transition.leave](ctx)
        if (leave) {
            this.navTl.add(leave)
        }

        // 2. 轉場切換點：更新 URL 狀態
        this.navTl.call(() => {
            if (!urlAlreadyUpdated) {
                this.router.push(to, true)
            }

            // 3. Enter 動畫串接
            const enter = this[transition.enter](ctx)
            if (enter) {
                this.navTl.add(enter)
            }
        })
    }

    /** 判斷路徑歸屬的 View 類型 */
    _viewOf(path) {
        if (path === '/') {
            return 'home'
        }
        if (path === '/about') {
            return 'about'
        }
        if (typeof path === 'string' && path.startsWith('/project/')) {
            return 'detail'
        }
        return null
    }

    /** 解析 URL 後段 slug 為對應的 Project 物件 */
    _parseProject(path) {
        const isDetail = this._viewOf(path) === 'detail'
        const slug = isDetail ? path.slice('/project/'.length) : null
        return slug
            ? projects.find((p) => {
                  return p.slug === slug
              })
            : null
    }

    // ─────────────────────────────────────────────────────────────
    // ③ Transition Animation Factories (Leave / Enter)
    // ─────────────────────────────────────────────────────────────

    leaveHome({ project }) {
        const tl = gsap.timeline()

        if (device.breakpoint === 'mobile') {
            this.carousel.disable()
            tl.to(
                this.carousel.units.map((u) => {
                    return u.material.uniforms.uOpacity
                }),
                { value: 0, duration: DURATION.fadeOut },
                0
            )
        } else {
            tl.add(this.carousel.leaveToDetail(project), 0)
            tl.add(this.project.listOut(), 0)
        }

        tl.add(this.footer.hide(), 0)

        return tl
    }

    enterHome() {
        this.carousel.enable()

        const tl = gsap.timeline()

        if (device.breakpoint !== 'mobile') {
            tl.add(this.project.listIn(), 0)
        }
        tl.add(this.carousel.fadeIn(), 0)
        tl.add(this.footer.show(), STAGGER.loose)

        return tl
    }

    leaveDetail() {
        if (typeof this.carousel?.resetOpacity === 'function') {
            this.carousel.resetOpacity()
        } else {
            this.carousel?.squares?.forEach((sq) => {
                gsap.killTweensOf(sq.material.uniforms.uOpacity)
                sq.material.uniforms.uOpacity.value = 0
            })
        }

        const tl = gsap.timeline()
        tl.add(this.detail.splitOut(), 0)
        tl.add(this.detail.imagesOut(), 0)

        return tl
    }

    // Hero 已在 leaveHome 的 leaveToDetail() 落地；這裡做 Carousel 那個 hero mesh → DOM <img> 的無縫換手
    enterDetail({ project }) {
        this.detail.setImages(project)

        const tl = gsap.timeline()
        const isMobile = device.breakpoint === 'mobile'

        if (!isMobile) {
            tl.call(() => {
                this.detail.whenHeroReady().then((stillCurrent) => {
                    if (!stillCurrent) {
                        return
                    }
                    this.detail.showHeroInstant()
                    this.carousel.hideHeroInstant()
                })
            })
        }
        tl.add(this.detail.imagesIn(isMobile), 0)
        tl.add(this.detail.splitIn(project), 0)

        return tl
    }

    leaveGallery() {
        const tl = gsap.timeline()

        tl.add(this.detail.splitOut(), 0)
        tl.add(this.detail.imagesOut(), 0)

        return tl
    }

    /** detail→detail (下一個專案)：沒有飛行動畫要保護，全部（含 hero）直接走 DOM crossfade */
    enterGallery({ project }) {
        this.detail.setImages(project)

        const tl = gsap.timeline()

        tl.add(this.detail.imagesIn(true), 0)
        tl.add(this.detail.splitIn(project), 0)

        return tl
    }

    leaveHomeToAbout() {
        const tl = gsap.timeline()

        tl.add(this.header.toClose(), 0)
        tl.add(this.carousel.fadeOut(), 0)
        if (device.breakpoint !== 'mobile') {
            tl.add(this.project.listOut(), 0)
        }
        tl.add(this.footer.hide(), 0)

        return tl
    }

    leaveDetailToAbout() {
        const tl = gsap.timeline()

        tl.add(this.header.toClose(), 0)
        tl.add(this.detail.imagesOut(), 0)
        tl.add(this.detail.splitOut(), 0)

        return tl
    }

    enterDetailFromAbout({ project }) {
        const tl = gsap.timeline()

        tl.add(this.detail.imagesIn(true), 0)
        tl.add(this.detail.splitIn(project), 0)

        return tl
    }

    leaveAbout() {
        const tl = gsap.timeline()

        tl.add(this.header.toAbout(), 0)
        tl.add(this.about.splitOut(), 0)

        return tl
    }

    enterAbout() {
        const tl = gsap.timeline()

        tl.add(this.about.splitIn(), 0)

        return tl
    }

    // ─────────────────────────────────────────────────────────────
    // ④ Intro & Deep-Link Handlers
    // ─────────────────────────────────────────────────────────────

    intro() {
        const project = this._parseProject(this.router.currentPath)
        if (project) {
            this._introDetail(project)
            return
        }
        if (this._viewOf(this.router.currentPath) === 'about') {
            this._introAbout()
            return
        }
        if (this._viewOf(this.router.currentPath) !== 'home') {
            this.router.replace('/')
        }
        this._introHome()
    }

    _introHome() {
        this.activeView = 'home'
        this.introTl = gsap.timeline()

        this.introTl
            .add(this.loader.fadeOut())
            .add(this.displaySquare(), '>+0.3')
            .add(this.loader.hide(), '>+0.3')
            .call(() => {
                this.squares.forEach((el) => {
                    if (el.material.uniforms.uHasImage.value >= 0.5) {
                        el.setColor()
                    }
                })
            })
            .call(() => {
                this.showPlaceholder()
                this.carousel = new Carousel(this.squares)
                this.reorderSquare().eventCallback('onComplete', () => {
                    this.carousel.enable()
                    this.displayUI()
                    this._wireCarousel()
                })
            })
    }

    _introDetail(project) {
        this.activeView = 'detail'
        this.introTl = gsap.timeline()

        this.introTl
            .add(this.loader.fadeOut())
            .add(this.loader.hide(), '>')
            .call(() => {
                this.squares.forEach((el) => {
                    if (el.material.uniforms.uHasImage.value >= 0.5) {
                        el.setColor()
                    }
                })
                this.carousel = new Carousel(this.squares)
                this._wireCarousel()

                this.header.show()
                if (device.breakpoint !== 'mobile') {
                    this.project.setProjects(projects)
                }

                this.carousel.disable()
                this.carousel.fadeStrength = 1
                this.carousel.units.forEach((u) => {
                    gsap.killTweensOf(u.material.uniforms.uOpacity)
                    u.material.uniforms.uOpacity.value = 0
                })

                // Deep-link 沒有進行中的轉場要保護，完全跳過 WebGL，直接淡入 DOM
                this.detail.setImages(project)
                this.detail.imagesIn(true)
                this.detail.splitIn(project)
            })
    }

    _introAbout() {
        this.activeView = 'about'
        this.introTl = gsap.timeline()

        this.introTl
            .add(this.loader.fadeOut())
            .add(this.loader.hide(), '>')
            .call(() => {
                this.squares.forEach((el) => {
                    if (el.material.uniforms.uHasImage.value >= 0.5) {
                        el.setColor()
                    }
                })
                this.carousel = new Carousel(this.squares)
                this._wireCarousel()

                this.carousel.disable()
                this.carousel.units.forEach((u) => {
                    gsap.killTweensOf(u.material.uniforms.uOpacity)
                    u.material.uniforms.uOpacity.value = 0
                })

                if (device.breakpoint !== 'mobile') {
                    this.project.setProjects(projects)
                }
                this.header.setAboutLabel('CLOSE')
                this.header.show()

                this.about.splitIn()
            })
    }

    /** 綁定 Carousel 的業務邏輯回調（斷點在 callback 內即時判斷，避免綁定當下的斷點被快取住） */
    _wireCarousel() {
        this.carousel.onHover = (project) => {
            if (device.breakpoint === 'mobile') {
                return
            }
            return this.project.hover(project)
        }
        this.carousel.onCenterChange = (project) => {
            if (device.breakpoint === 'mobile') {
                return
            }
            return this.project.update(project)
        }
        this.carousel.onSelect = (project) => {
            return this.requestNavigate(`/project/${project.slug}`)
        }
    }

    displaySquare() {
        const opacities = this.squares
            .filter((sq) => {
                return sq.material.uniforms.uHasImage.value >= 0.5
            })
            .map((sq) => {
                return sq.material.uniforms.uOpacity
            })

        return gsap.to(opacities, {
            value: 1,
            duration: DURATION.snap,
            ease: EASE.out,
            stagger: STAGGER.loose
        })
    }

    showPlaceholder() {
        this.squares.forEach((sq) => {
            if (sq.material.uniforms.uHasImage.value < 0.5) {
                sq.material.uniforms.uOpacity.value = 1
            }
        })
    }

    reorderSquare() {
        const c = this.carousel
        const targetSpacing = c.SPACING
        const targetR = c.R
        const homePos = c.group.position.clone()
        const homeQuat = c.group.quaternion.clone()

        const startR = 40
        c.SPACING = 0
        c.R = startR
        c.group.position.set(0, 0, 0)
        c.group.rotation.set(0, 0, 0)
        const startQuat = c.group.quaternion.clone()

        c._position = c.count - 1
        c._target = c.count - 1

        const rot = { t: 0 }
        const curve = { k: 1 / startR }

        return gsap
            .timeline()
            .to(c, { SPACING: targetSpacing, duration: DURATION.reveal, ease: EASE.out }, 0)
            .to(c, { fadeStrength: 1, duration: DURATION.spread, ease: EASE.out }, 0)
            .to(
                curve,
                {
                    k: 1 / targetR,
                    duration: DURATION.move,
                    ease: EASE.move,
                    onUpdate: () => {
                        c.R = 1 / curve.k
                    }
                },
                '>'
            )
            .to(
                c.group.position,
                {
                    x: homePos.x,
                    y: homePos.y,
                    z: homePos.z,
                    duration: DURATION.move,
                    ease: EASE.move
                },
                '<'
            )
            .to(
                rot,
                {
                    t: 1,
                    duration: DURATION.move,
                    ease: EASE.move,
                    onUpdate: () => {
                        return c.group.quaternion.slerpQuaternions(startQuat, homeQuat, rot.t)
                    }
                },
                '<'
            )
    }

    displayUI() {
        const tl = gsap.timeline()
        tl.add(this.header.show(), 0)
        tl.add(this.footer.show(), 0)

        if (device.breakpoint !== 'mobile') {
            this.project.setProjects(projects)
            tl.add(this.project.listIn(projects[projects.length - 1]), '+0.2')
        }

        return tl
    }

    // ─────────────────────────────────────────────────────────────
    // ⑤ Lifecycle & Frame Update Loop
    // ─────────────────────────────────────────────────────────────

    setCarouselMode(mode) {
        this.carousel?.setMode(mode)
    }

    /** 每幀由 RAF Loop 驅動：驅動 Active View 的 3D 物件更新 */
    update() {
        if (this.activeView === 'detail') {
            this.detail?.update()
        } else {
            this.carousel?.update()
        }

        for (const sq of this.squares) {
            sq.update()
        }
    }

    /** 視窗尺寸改變時同步轉發 */
    resize() {
        this.carousel?.resize()
        this.detail?.resize()
        this.about?.resize()

        const currentBreakpoint = device.breakpoint

        if (this._lastBreakpoint === 'mobile' && currentBreakpoint !== 'mobile') {
            // 延遲到下一幀執行，確保 DOM layout 與字型重新測量完畢
            requestAnimationFrame(async () => {
                await document.fonts.ready
                this.project.setProjects(projects)

                if (this.activeView === 'home') {
                    this.project.listIn(projects[projects.length - 1])
                }
            })
        } else if (this._lastBreakpoint !== 'mobile' && currentBreakpoint === 'mobile') {
            this.project._resetHover?.()
        }

        this._lastBreakpoint = currentBreakpoint
    }

    /** 釋放資源 */
    destroy() {
        this.navTl?.kill()
        this.introTl?.kill()
        this.squares.forEach((sq) => {
            return sq.destroy?.()
        })
        this.carousel?.destroy?.()
    }
}
